/**
 * SignInForm Component

 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import styles from './signInForm.module.css';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Bounce, Id, toast, ToastContainer } from 'react-toastify';
import { debounce } from 'lodash';

// Constants
import { 
  NAVIGATION_ROUTES, 
  SIGN_UP_ACTION, 
  DEFAULT_NUMBER_VALIDATION,
  LANGUAGE,
  RESENT_OTP_TIME,
  RESEND_OTP_DEBOUNCE_DELAY,
  SIGN_IN_CODES,
  SIGNUP
} from '../../../constant';

// Assets
import arrowRightImg from '../../../assets/images/verticaltv/arrow-right-img.webp';

// Hooks
import useExtraFunctions from '../../../hooks/useExtraFunctions';
import useLoginStore from '../../../storage/store/useLoginStore';
import useSendOtp from '../../../api/otp';
import { useAuthenticateUserApi } from '../../../api/useAuthApi';
import { useRequestOtpApi } from '../../../api/useRequestOtpApi';

// Utils
import { isExternalUrl, handleLinkClick } from '../../../utils/externalLinkHandler';
import { setAuthTokenInHeader } from '../../../api';
import { dispatchToken } from '../../../storage/tokenState';

// Analytics - CleverTap
import { CLEVERTAP_EVENTS } from '../../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent, setUserMobile } from '../../../utils/cleverTapHelper';

// Analytics - Facebook Pixel
import { FbPixelEvent_CompleteRegistration } from '../../../utils/FbPixel/fbPixel';

// Analytics - AppsFlyer
import { fireCompleteRegistrationEvent } from '../../../utils/SmartScriptAppFlyer/appsFlyerEvents';

// Kafka Events API
import { pushKafkaEvent } from '../../../api/pushKafkaEvent';
import { GA_EVENTS, pushGAEvent } from '../../../utils/gaHelper';

// Declare global types for WebOTP API
declare global {
  interface CredentialRequestOptions {
    otp?: {
      transport: string[];
    };
  }
  interface Credential {
    code?: string;
  }
}
// Add CleverTap profile push function
const pushCleverTapProfile = (phoneNumber: string, identity: string) => {
  if (typeof window === "undefined") return;

  const clevertap = (window as any).clevertap;
  if (!clevertap?.profile) {
    console.warn("CleverTap is not initialized yet.");
    return;
  }

  try {
    clevertap.onUserLogin.push({
      "Site": {
        "Identity": identity,
        "Phone": `+91${phoneNumber}`,
        "MSG-email": true,
        "MSG-push": true,
        "MSG-sms": true,
        "MSG-whatsapp": true,
      }
    })
    console.log(`%cCleverTap Profile Pushed for: ${phoneNumber}`, "color:green;font-weight:bold");
  } catch (error) {
    console.error("%cFailed to push CleverTap profile:", "color:red;font-weight:bold", error);
  }
};

interface SignInFormProps {
  /** Title text displayed above the form */
  title?: string;
  /** Subtitle text displayed below the title */
  subtitle?: string;
  /** Optional callback function called after successful login */
  onLoginSuccess?: () => void;
  /** Whether to show the screen view event (set false if parent already tracks it) */
  trackScreenView?: boolean;
  /** Custom class name for the container */
  className?: string;
  /** Whether the component should be sticky at bottom */
  isSticky?: boolean;
}

type AuthStep = 'phone' | 'otp';

const SignInForm: React.FC<SignInFormProps> = ({
  title = 'Sign in',
  subtitle = 'to continue watching',
  onLoginSuccess,
  trackScreenView = true,
  className,
  isSticky = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  // ========== STATE ==========
  const [currentStep, setCurrentStep] = useState<AuthStep>('phone');
  
  // Phone state
  const [mobileNumber, setMobileNumber] = useState('');
  const [focusCheck, setFocusCheck] = useState(false);
  const [isDeclarationChecked] = useState(true);
  const [phoneErrorShow, setPhoneErrorShow] = useState(true);
  const [phoneLoading, setPhoneLoading] = useState(false);

  // OTP state
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [countdown, setCountdown] = useState<number>(RESENT_OTP_TIME);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(true);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [otpLoading, setOtpLoading] = useState(false);

  // Scroll state - show terms when user scrolls down
  const [showTerms, setShowTerms] = useState(false);
  
  // Backdrop blur state - shown when user focuses on input to type number
  const [showBackdrop, setShowBackdrop] = useState(false);

  // ========== REFS ==========
  const mobileNumberRef = useRef<HTMLInputElement>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const toastId = useRef<Id>();
  const phoneEventTrackedRef = useRef(false);
  const otpEventTrackedRef = useRef(false);
  const otpInputTrackedRef = useRef(false);
  const inputRefOtp = useRef<(HTMLInputElement | null)[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const popupViewTrackedRef = useRef<boolean>(false);

  // ========== HOOKS ==========
  const { validateMobile, getPartnerId } = useExtraFunctions();
  const { mobileNumberState, setMobileNumberState } = useLoginStore();
  const { requestOtp } = useSendOtp();

  // OTP API hooks - only initialize when we have a phone number
  const { refetch, isLoading: isAuthLoading } = useAuthenticateUserApi(
    mobileNumber,
    SIGN_UP_ACTION,
    LANGUAGE,
    true,
    otp.join(''),
    getPartnerId(),
    SIGNUP
  );

  const { refetchOTP } = useRequestOtpApi(mobileNumber, SIGN_UP_ACTION, true, LANGUAGE);

  // ========== TOGGLE TERMS ON TAP/CLICK ==========
  const handleFormAreaClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent backdrop click handler from firing
    if (currentStep === 'phone') {
      setShowTerms(prev => !prev);
    }
  }, [currentStep]);

  // ========== BACKDROP CLICK HANDLER ==========
  // Only dismiss backdrop on phone step, not on OTP step (use back button instead)
  const handleBackdropClick = useCallback(() => {
    if (currentStep === 'phone') {
      setShowBackdrop(false);
    }
  }, [currentStep]);

  // ========== DISABLE BODY SCROLL WHEN BACKDROP IS VISIBLE ==========
  useEffect(() => {
    if (showBackdrop && isSticky) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Lock body in place to prevent keyboard scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overflow = 'hidden';
      
      // Prevent scroll/wheel events on document level
      const preventScroll = (e: Event) => {
        e.preventDefault();
      };
      
      const preventTouchMove = (e: TouchEvent) => {
        // Allow touch on form container (inputs, buttons)
        const target = e.target as HTMLElement;
        const isInsideForm = formContainerRef.current?.contains(target);
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON';
        
        if (!isInsideForm || !isInput) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      return () => {
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventTouchMove);
        // Restore body position and scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        document.documentElement.style.overflow = '';
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    } else {
      // Enable body scroll
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
    };
  }, [showBackdrop, isSticky]);

  // ========== PREVENT SCROLL ON FORM CONTAINER ==========
  useEffect(() => {
    const container = formContainerRef.current;
    if (!container || !isSticky) return;

    // Prevent touchmove from scrolling the page when touching the form
    const preventScroll = (e: TouchEvent) => {
      // Allow scrolling inside input fields
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      if (!isInput) {
        e.preventDefault();
      }
    };

    container.addEventListener('touchmove', preventScroll, { passive: false });
    
    return () => {
      container.removeEventListener('touchmove', preventScroll);
    };
  }, [isSticky]);

  // Always show terms in OTP step
  useEffect(() => {
    if (currentStep === 'otp') {
      setShowTerms(true);
    }
  }, [currentStep]);

  // ========== AUTO-READ SMS HOOK ==========
  useEffect(() => {
    if (currentStep !== 'otp') return;

    const handleAutoRead = async () => {
      if ('OTPCredential' in window) {
        abortControllerRef.current = new AbortController();
        
        try {
          // Track event when auto-read popup is viewed (only once)
          if (!popupViewTrackedRef.current) {
            popupViewTrackedRef.current = true;
            trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_POPUP_VIEW_AUTO_READ);
          }
          
          const credential = await navigator.credentials.get({
            otp: { transport: ['sms'] },
            signal: abortControllerRef.current?.signal,
          });

          // Track event when user responds to auto-read popup (accepted)
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_POPUP_AUTO_READ_RESPONDE);
          
          if (credential && 'code' in credential && credential.code) {
            const code = credential.code;
            if (code.length === 6) {
              setOtp(code.split(''));
            }
          }
        } catch (err) {
          // Track event when user responds to auto-read popup (rejected/cancelled)
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_POPUP_AUTO_READ_RESPONDE);
          console.log('WebOTP error:', err);
        }
      }
    };

    handleAutoRead();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [currentStep]);

  // ========== EFFECTS ==========
  
  // Populate mobile number from store
  useEffect(() => {
    if (mobileNumberState && currentStep === 'phone') {
      setMobileNumber(mobileNumberState);
      setFocusCheck(true);
    }
  }, [mobileNumberState, currentStep]);

  // Note: WEB_EVENT_NEW_SIGN_UP_VIEW is fired in handlePhoneSubmit when user clicks proceed
  // This useEffect is kept for potential future screen view tracking if needed
  // useEffect(() => {
  //   if (!trackScreenView || phoneEventTrackedRef.current || currentStep !== 'phone') {
  //     return;
  //   }
  //   phoneEventTrackedRef.current = true;
  // }, [trackScreenView, currentStep]);

  // Track OTP screen view event
  useEffect(() => {
    if (currentStep !== 'otp' || otpEventTrackedRef.current) {
      return;
    }
    otpEventTrackedRef.current = true;
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_SCREEN_VIEW_OTP);
  }, [currentStep]);

  // Phone number validation
  useEffect(() => {
    if (currentStep !== 'phone') return;
    
    const validTime = setTimeout(() => {
      if (mobileNumber.length === 10) {
        if (!validateMobile(mobileNumber)) {
          setPhoneErrorShow(true);
          toastId.current = toast.error("Invalid mobile number", {
            position: "bottom-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            style: { borderRadius: '10px', marginBottom: '20px' }
          });
        } else {
          setPhoneErrorShow(false);
        }
      }
    }, 100);

    return () => {
      clearTimeout(validTime);
      if (toastId.current) {
        toast.dismiss(toastId.current);
      }
    };
  }, [mobileNumber, validateMobile, currentStep]);

  // OTP countdown timer
  useEffect(() => {
    if (currentStep !== 'otp') return;
    
    if (countdown > 0 && isOtpSent) {
      const timerId = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timerId);
    }
    if (countdown === 0) setIsOtpSent(false);
  }, [countdown, isOtpSent, currentStep]);

  // Focus first OTP input when entering OTP step
  useEffect(() => {
    if (currentStep === 'otp') {
      setTimeout(() => {
        const firstInput = inputRefOtp.current[0];
        if (firstInput) {
          firstInput.focus();
          firstInput.click();
        }
      }, 300);

      toast.success("OTP sent successfully", {
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        theme: "colored",
        transition: Bounce,
      });
    }
  }, [currentStep]);

  // ========== PHONE HANDLERS ==========
  
  const handleSendOtp = useCallback(debounce(async () => {
    toastId.current && toast.dismiss(toastId.current);
    
    try {
      await requestOtp(mobileNumber, 'en', SIGN_UP_ACTION, false);
      toast.dismiss(toastId.current);
      
      // Move to OTP step
      setCurrentStep('otp');
      setPhoneLoading(false);
    } catch (error) {
      console.log("error sign up", error);
      setPhoneLoading(false);
      toast.error("OTP send failed", {
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
        style: { borderRadius: '10px', marginBottom: '20px' }
      });
    }
  }, 200), [mobileNumber, requestOtp]);

  const handlePhoneSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneLoading(true);

    const regex = new RegExp(DEFAULT_NUMBER_VALIDATION);
    const tempNumber = `+91${mobileNumber}`;

    if (!regex.test(tempNumber)) {
      setPhoneErrorShow(true);
      mobileNumberRef.current?.focus();
      setTimeout(() => {
        toast.error("Invalid mobile number", {
          position: "bottom-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
          style: { borderRadius: '10px', marginBottom: '20px' }
        });
      }, 400);
      setPhoneLoading(false);
      return false;
    }

    setPhoneErrorShow(false);
    setMobileNumberState(mobileNumber);

    // Track CleverTap event when user enters number and clicks proceed
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_NEW_SIGN_UP_VIEW, {
      [CT_PARAMS.MOBILE_NUMBER]: mobileNumber
    });

    // Track CleverTap event when user proceeds with phone number
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_PHONE_PROCEED, {
      [CT_PARAMS.MOBILE_NUMBER]: mobileNumber
    });

    handleSendOtp();
  }, [mobileNumber, handleSendOtp, setMobileNumberState]);

  // Show backdrop blur when user focuses on input
  const handlePhoneInputFocus = useCallback(() => {
    if (!showBackdrop) {
      setShowBackdrop(true);
    }
  }, [showBackdrop]);

  const handlePhoneInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobileNumber(value);
    setFocusCheck(true);

    if (!isDeclarationChecked) {
      setPhoneErrorShow(true);
    } else if (value.length !== 10) {
      setPhoneErrorShow(true);
    } else if (!validateMobile(value)) {
      setPhoneErrorShow(true);
    } else {
      setPhoneErrorShow(false);
    }
  }, [isDeclarationChecked, validateMobile]);

  // ========== OTP HANDLERS ==========

  const handleOtpInputChange = (value: string, index: number) => {
    if (!/^[0-9]*$/.test(value)) return;
    const newOtp = [...otp];

    if (value.length > 1) {
      for (let i = 0; i < value.length && index + i < newOtp.length; i++) {
        newOtp[index + i] = value[i];
      }
    } else {
      newOtp[index] = value;
    }
    setOtp(newOtp);

    // Track OTP input event only once when complete 6-digit OTP is entered
    const isComplete = newOtp.every((digit) => digit !== '');
    if (isComplete && newOtp.length === 6 && !otpInputTrackedRef.current) {
      otpInputTrackedRef.current = true;
      trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_OTP_INPUT);
    }

    // Move focus forward
    if (value.length > 0) {
      const nextIndex = index + (value.length > 1 ? value.length - 1 : 1);
      if (nextIndex < newOtp.length) {
        setTimeout(() => {
          inputRefOtp.current[nextIndex]?.focus();
        }, 0);
      }
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    handleOtpInputChange(pastedData, index);
  };

  const handleOtpBackspace = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (otp[index] === '' && index > 0) {
        inputRefOtp.current[index - 1]?.focus();
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsOtpSent(true);
      setCountdown(RESENT_OTP_TIME);
      await refetchOTP();
      toast.success(t("otpResent"));
    } catch (err) {
      toast.error(t("otpResentFailed"));
    }
  };

  const throttledResendOtp = useCallback(debounce(handleResendOtp, RESEND_OTP_DEBOUNCE_DELAY), [refetchOTP]);

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpLoading(true);
    
    try {
      const response = await refetch();
      const authResponse = response?.data;

      if (authResponse?.success && authResponse?.flag === true &&
        authResponse?.errorCode === SIGN_IN_CODES.SUCCESS_CODE) {
        
        const signUpData = (authResponse as any)?.signUpData || {};
        const accessToken = signUpData?.accessToken || '';
        const tokenData = signUpData || {};
        const _id = signUpData?._id || '';
        
        
        const postSignupConfig = (authResponse as any)?.postConfig || 
                                 (response?.data as any)?.postConfig || 
                                 (response as any)?.data?.response?.postConfig || 
                                 {};
        
        const subscriptionData = {
          userSubscriptionData: postSignupConfig?.userSubscriptionData,
          activeSubscriptionData: postSignupConfig?.activeSubscriptionData,
          subscriptionFeatureConfig: postSignupConfig?.subscriptionFeatureConfig
        };
        
        setAuthTokenInHeader(accessToken, _id);
        
        const tokenValidityDuration = signUpData?.validity || 0;
        const tokenDataWithTimestamp = {
          ...tokenData,
          validity: tokenValidityDuration > 0 
            ? Date.now() + tokenValidityDuration - 10
            : 0
        };
        
        dispatchToken(tokenDataWithTimestamp);

        // Track CleverTap event when user successfully logs in
        const source = CT_PARAMS.SCREEN_SIGNUP;
        const userId = signUpData?._id ||  '';
        const userIdNumeric = signUpData?.zupeeUserId || '';
        pushCleverTapProfile(mobileNumber as string, userIdNumeric);
        
        // Extract user subscription state from userSubscriptionData (e.g., 'TRIAL_CANCELED', 'ACTIVE', etc.)
        const userSubscriptionState = postSignupConfig?.userSubscriptionData?.userSubscriptionState?.state || '';
        
        // Track CleverTap login event with mobile number
        // Note: titleId is automatically added by trackCleverTapEvent for all WEB_EVENT_* events
        trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_LOGIN, {
          [CT_PARAMS.SOURCE]: source,
          [CT_PARAMS.USER_STATE]: userSubscriptionState,
          [CT_PARAMS.USER_ID]: userIdNumeric,
          [CT_PARAMS.MOBILE_NUMBER]: mobileNumber // Send mobile number in login event
        });

        // Persist mobile number for all subsequent CT events
        if (mobileNumber) {
          setUserMobile(mobileNumber);
        }

        // Track Facebook Pixel events
        FbPixelEvent_CompleteRegistration();

        // Fire AppsFlyer CompleteRegistration event
        fireCompleteRegistrationEvent({ method: 'otp_verification', page: 'subscription_signin' });

        // Push sign_up or WEB_EVENT_LOGIN event to Kafka based on isNewSignup (non-blocking)
        // If isNewSignup is true -> 'sign_up', if false -> 'WEB_EVENT_LOGIN'
        const kafkaEventSubType = signUpData?.isNewSignup === true ? 'sign_up' : 'WEB_EVENT_LOGIN';
        pushKafkaEvent(kafkaEventSubType, userId, mobileNumber, signUpData?.zupeeUserId).catch(err => {
          console.log(`[SignInForm] Kafka ${kafkaEventSubType} event failed (non-critical):`, err);
        });

        // Track GA event for OTP success
        pushGAEvent(GA_EVENTS.OTP_SUCCESS, {
          user_state: userSubscriptionState,
          source: source,
          user_id: userIdNumeric,
          user_mobile: mobileNumber,
        });

        // Call success callback if provided
        onLoginSuccess?.();
        
        // Navigate based on subscription status
        const userSubscriptionData = subscriptionData?.userSubscriptionData || subscriptionData?.activeSubscriptionData;
        const currentTimestamp = Date.now();
        const hasSubscriptionData = userSubscriptionData && typeof userSubscriptionData === 'object';
        const isActive = userSubscriptionData?.isActive === true;
        let hasValidValidity = true;
        if (userSubscriptionData?.validityTill) {
          hasValidValidity = userSubscriptionData.validityTill > currentTimestamp;
        }
        const isSubscriptionActive = hasSubscriptionData && isActive && hasValidValidity;

        // Check if we're on a trial page to navigate to the correct routes
        const isTrialPage = location.pathname.startsWith('/trial');
        
        if (isSubscriptionActive) {
          const subscribedRoute = isTrialPage ? '/trial/subscribed' : NAVIGATION_ROUTES.REFERRAL_SUBSCRIBED;
          navigate(subscribedRoute, { replace: true });
        } else {
          const paymentRoute = isTrialPage ? '/trial/subscription-payment' : NAVIGATION_ROUTES.REFERRAL_SUBSCRIPTION_PAYMENT;
          navigate(paymentRoute, { replace: true });
        }
      } else if (authResponse?.success && authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
        if (authResponse?.errorCode === SIGN_IN_CODES.EXISTING_USER_ERROR_CODE) {
          setOtp(Array(6).fill(''));
          setOtpLoading(false);
          return;
        }
        setOtp(Array(6).fill(''));
        toast.error(authResponse.error, { toastId: authResponse.error });
        setOtpLoading(false);
      }
    } catch (error) {
      setOtp(Array(6).fill(''));
      toast.error(t("errorOccurred"));
      setOtpLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setCurrentStep('phone');
    setOtp(Array(6).fill(''));
    setCountdown(RESENT_OTP_TIME);
    setIsOtpSent(true);
    otpEventTrackedRef.current = false;
    otpInputTrackedRef.current = false;
    // Hide backdrop when going back to phone step
    setShowBackdrop(false);
  };

  // ========== COMPUTED VALUES ==========
  const isPhoneFormValid = mobileNumber.length === 10 && !phoneErrorShow && isDeclarationChecked;
  const isOtpComplete = otp.every((digit) => digit !== '');

  // ========== SVG ICONS ==========
  const ArrowRightIcon = () => (
    <img 
      src={arrowRightImg} 
      alt="arrow" 
      className={styles.arrowIcon} 
      style={{ flexShrink: 0, display: 'block' }}
    />
  );

  const BackArrowIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.backIcon}>
      <path d="M15 19L8 12L15 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const IndiaFlagIcon = () => (
    <svg 
      width="24" 
      height="18" 
      viewBox="0 0 24 18" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      className={styles.flagIcon}
      style={{ flexShrink: 0, display: 'block' }}
      preserveAspectRatio="xMidYMid meet"
    >
      <rect width="24" height="6" fill="#FF9933"/>
      <rect y="6" width="24" height="6" fill="#FFFFFF"/>
      <rect y="12" width="24" height="6" fill="#138808"/>
      <circle cx="12" cy="9" r="2.5" fill="#000080"/>
      <circle cx="12" cy="9" r="2" fill="#FFFFFF"/>
      <circle cx="12" cy="9" r="0.5" fill="#000080"/>
    </svg>
  );

  // ========== GRADIENT BACKGROUND ==========
  const GradientBackground = () => (
    <div className={styles.gradientBackgroundSvg}>
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 360 300" fill="none" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="signInGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4F5568" stopOpacity="0.6"/>
            <stop offset="30%" stopColor="#0D0E11" stopOpacity="0.95"/>
            <stop offset="100%" stopColor="#0D0E11" stopOpacity="1"/>
          </linearGradient>
          <radialGradient id="signInGradient2" cx="90%" cy="80%" r="60%">
            <stop offset="0%" stopColor="#8B2A6F" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="#0D0E11" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="signInGradient3" cx="10%" cy="20%" r="50%">
            <stop offset="0%" stopColor="#4F5568" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#0D0E11" stopOpacity="0"/>
          </radialGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#signInGradient1)"/>
        <rect width="100%" height="100%" fill="url(#signInGradient2)"/>
        <rect width="100%" height="100%" fill="url(#signInGradient3)"/>
      </svg>
    </div>
  );

  // ========== RENDER ==========
  const containerClass = isSticky ? styles.stickyContainer : '';

  return (
    <>
      {/* Backdrop overlay with blur - click to dismiss */}
      {isSticky && (
        <div 
          className={clsx(styles.backdrop, showBackdrop ? styles.backdropVisible : styles.backdropHidden)}
          onClick={handleBackdropClick}
        />
      )}
      
      <div className={clsx(containerClass, className)} ref={formContainerRef} onClick={handleFormAreaClick}>
        <div className={styles.signInFormContainer}>
        <GradientBackground />
        
        <div className={clsx(styles.contentWrapper, styles.fadeIn)} key={currentStep}>
          {currentStep === 'phone' ? (
            // ========== PHONE INPUT VIEW ==========
            <>
              <div className={styles.titleSection}>
                <h2 className={styles.title}>{title}</h2>
                <p className={styles.subtitle}>{subtitle}</p>
              </div>

              <form onSubmit={handlePhoneSubmit} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className={styles.phoneInputWrapper}>
                  <div className={styles.countryCode}>
                    <IndiaFlagIcon />
                    <span className={styles.countryCodeText}>+91</span>
                  </div>

                  <input
                    ref={mobileNumberRef}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={styles.phoneInput}
                    placeholder="Phone number"
                    value={mobileNumber}
                    onChange={handlePhoneInputChange}
                    onFocus={handlePhoneInputFocus}
                    maxLength={10}
                    autoComplete="tel"
                  />

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={!isPhoneFormValid || phoneLoading}
                  >
                    {phoneLoading ? (
                      <div className={styles.loadingSpinner} />
                    ) : (
                      <ArrowRightIcon />
                    )}
                  </button>
                </div>
              </form>

              {/* Terms Section - toggle on tap/click with smooth transition */}
              <div className={clsx(styles.termsSection, showTerms ? styles.termsVisible : styles.termsHidden)}>
                <span className={styles.termsText}>
                  By entering your number, you're agreeing to our{' '}
                  {isExternalUrl(NAVIGATION_ROUTES.REFERRAL_TNC) ? (
                    <a
                      href={NAVIGATION_ROUTES.REFERRAL_TNC}
                      onClick={(e) => handleLinkClick(NAVIGATION_ROUTES.REFERRAL_TNC, e)}
                      className={styles.termsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                  ) : (
                    <NavLink
                      to={NAVIGATION_ROUTES.REFERRAL_TNC}
                      state={{ url: NAVIGATION_ROUTES.REFERRAL_TNC }}
                      className={styles.termsLink}
                    >
                      Terms & Conditions
                    </NavLink>
                  )}
                </span>
              </div>
            </>
          ) : (
            // ========== OTP INPUT VIEW ==========
            <>
              <div className={styles.otpHeader}>
                <button type="button" className={styles.backButton} onClick={handleBackToPhone}>
                  <BackArrowIcon />
                </button>
                <h2 className={styles.otpTitle}>Enter OTP</h2>
              </div>

              <form onSubmit={handleOtpSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                {/* Hidden input for OTP autofill */}
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  name="otp"
                  id="otp-autocomplete-sticky"
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0, left: '-9999px' }}
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(e) => {
                    const code = e.target.value.replace(/\D/g, '').slice(0, 6);
                    if (code.length === 6) {
                      setOtp(code.split(''));
                      setTimeout(() => {
                        if (inputRefOtp.current[5]) {
                          inputRefOtp.current[5].focus();
                        }
                      }, 100);
                    }
                  }}
                />

                <div className={styles.otpInputContainer}>
                  {otp.map((digit, index) => (
                    <div key={index} className={styles.otpInputWrapper}>
                      {/* Show dot for empty inputs */}
                      {!digit && (
                        <div className={styles.otpCursor}></div>
                      )}
                      <input
                        className={clsx(
                          styles.otpInput,
                          (digit || focusedIndex === index) && styles.active,
                          digit && styles.filled,
                          styles.hideCaret
                        )}
                        type="number"
                        maxLength={1}
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleOtpInputChange(e.target.value, index)}
                        onKeyDown={(e) => handleOtpBackspace(e, index)}
                        onPaste={(e) => handleOtpPaste(e, index)}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => setFocusedIndex(-1)}
                        ref={(el) => (inputRefOtp.current[index] = el)}
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        pattern="\d*"
                      />
                    </div>
                  ))}
                </div>

                <div className={styles.otpInfoSection}>
                  <span className={styles.resendText}>OTP sent to</span>
                  <span className={styles.phoneNumberDisplay}>{mobileNumber}</span>
                  <span className={styles.separator}>•</span>
                  {isOtpSent ? (
                    <span className={styles.resendText}>Resend in {countdown}s</span>
                  ) : (
                    <button type="button" className={styles.resendButton} onClick={throttledResendOtp}>
                      Resend
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles.verifyButton}
                  disabled={!isOtpComplete || otpLoading || isAuthLoading}
                >
                  {otpLoading || isAuthLoading ? (
                    <div className={styles.loadingSpinner} style={{ margin: '0 auto' }} />
                  ) : (
                    'Verify'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <ToastContainer position="bottom-center" />
      </div>
    </div>
    </>
  );
};

export default SignInForm;
